import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { transparentize } from 'polished';
import styled from 'styled-components';

import { TenantEntities } from '@/features/canvas/api/tenant-sso';
import { deleteGroup } from '@/features/publicLinks/api';
import Button from '@/features/ui/components/Button/Button';
import { ConfirmationDialog } from '@/features/ui/components/ConfirmationDialog';
import { useFormContext } from '@/features/ui/components/Form';
import { FormTextInput } from '@/features/ui/components/FormTextInput';

import EditGroup from './components/EditGroup';
import GroupMembersDialog from './components/GroupMembersDialog';

const GroupsTable = ({
  data,
  isAddEntityActive,
  setIsAddEntityActive,
  setEntities,
  searchText,
  setIsEdit,
  isEdit,
}: {
  data: TenantEntities;
  isAddEntityActive: boolean;
  isEdit: boolean;
  searchText: string;
  setEntities: Dispatch<SetStateAction<TenantEntities>>;
  setIsAddEntityActive: Dispatch<SetStateAction<boolean>>;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const [activeGroup, setActiveGroup] = useState<number | null>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { reset } = useFormContext();

  const { mutateAsync } = useMutation(({ id }: { id: number }) => deleteGroup(id), {
    onSuccess: () => {
      setIsDeleting(false);
      setEntities(prev => ({
        ...prev,
        groups: prev.groups.filter(item => item.id !== activeGroup),
      }));
      setActiveGroup(null);
    },
  });

  useEffect(() => {
    setIsEditing(isAddEntityActive);
  }, [isAddEntityActive]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(event.target as Node) && buttonRef.current) {
        buttonRef.current.click();
      }
    };

    if (isEditing) {
      document.addEventListener('click', handleClickOutside);
    } else {
      reset();
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isEditing, setIsAddEntityActive, reset]);

  const filteredData = useMemo(
    () => data.groups.filter(item => item.name.toLowerCase().includes(searchText.toLowerCase())),
    [data.groups, searchText]
  );

  return (
    <>
      <TableHead>
        <TableRow>
          <StyledTableCellHeader>{t('access.name')}</StyledTableCellHeader>
          <StyledTableCellHeader align="center">{t('access.group_members')}</StyledTableCellHeader>
          <StyledTableCellHeader align="center">{t('access.duplicate')}</StyledTableCellHeader>
          <StyledTableCellHeader align="center">{t('access.delete')}</StyledTableCellHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {isAddEntityActive && (
          <TableRow ref={rowRef}>
            <StyledAddEntityTableCell>
              <StyledFormInput
                name="name"
                placeholder="Name"
                required
                rules={{ required: { value: true, message: t('form.field_required_validation') } }}
              />
            </StyledAddEntityTableCell>
            <StyledAddEntityTableCell style={{ display: 'none', pointerEvents: 'none' }}>
              <Button ref={buttonRef} label="" type="submit" />
            </StyledAddEntityTableCell>
            <StyledAddEntityTableCell />
            <StyledAddEntityTableCell />
            <StyledAddEntityTableCell>
              <IconWrapper
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  marginTop: '1rem',
                }}
              >
                <XMarkIcon width={20} height={20} onClick={() => setIsAddEntityActive(false)} />
              </IconWrapper>
            </StyledAddEntityTableCell>
          </TableRow>
        )}
        {filteredData.map(item => (
          <EditGroup
            key={item.id}
            item={item}
            setIsEdit={setIsEdit}
            isEdit={isEdit}
            setActiveGroup={setActiveGroup}
            setEntities={setEntities}
            setIsDeleting={setIsDeleting}
          />
        ))}
      </TableBody>
      {activeGroup && !isDeleting && (
        <GroupMembersDialog
          groupId={activeGroup}
          onClose={() => setActiveGroup(null)}
          members={data.viewers}
        />
      )}
      {isDeleting && activeGroup && (
        <ConfirmationDialog
          open={isDeleting}
          okButtonText={t('yes_sure')}
          keepMounted={false}
          description={t('access.delete_group')}
          handleOk={() => mutateAsync({ id: Number(activeGroup) })}
          id="delete-viewer-confirm-dialog"
          onClose={() => {
            setActiveGroup(null);
            setIsDeleting(false);
          }}
        />
      )}
    </>
  );
};

const StyledFormInput = styled(FormTextInput)`
  padding: 0 0.5rem;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => transparentize(0.7, theme.palette.brand.corePPTCopy)};
  font-size: 2rem;

  &:focus-within {
    border: 1px solid ${({ theme }) => transparentize(0.7, theme.palette.brand.corePPTCopy)};
    background: ${({ theme }) => theme.palette.brand.white};
  }
`;

const StyledTableCell = styled(TableCell)`
  * {
    font-size: 2rem;
    color: ${({ theme }) => theme.palette.brand.textSecondary};
  }
`;

const StyledAddEntityTableCell = styled(StyledTableCell)`
  border: none;
  vertical-align: top;
`;

const IconWrapper = styled.div`
  cursor: pointer;
  width: 2.2rem;
  height: 2.2rem;

  svg {
    width: 100%;
    height: 100%;
    path,
    rect {
      stroke: ${({ theme }) => theme.palette.brand.primary};
    }
  }

  &:hover {
    svg {
      path,
      rect {
        stroke: ${({ theme }) => theme.palette.brand.editorTabPrimary};
      }
    }
  }
`;

const StyledTableCellHeader = styled(TableCell)`
  font-size: 2rem;
  color: ${({ theme }) => theme.palette.brand.primary};
  font-weight: bold;
`;

export default GroupsTable;
